import * as THREE from 'three'
import Experience from '../Experience.js'
import vertexShader   from '../../shaders/background/vertex.glsl'
import fragmentShader from '../../shaders/background/fragment.glsl'

/**
 * Background
 * Fullscreen quad rendered behind the scene using FBM noise shader.
 * Sized dynamically to fill the viewport based on camera FOV and distance.
 * Reacts to smoothed mouse position via uMouse uniform.
 */
export default class Background {
    constructor() {
        this.experience = new Experience()
        this.scene      = this.experience.scene
        this.sizes      = this.experience.sizes
        this.time       = this.experience.time
        this.camera     = this.experience.camera
        this.mouse      = this.experience.mouse

        this.setGeometry()
        this.setMaterial()
        this.setMesh()
        this.setResize()
    }

    setGeometry() {
        this.geometry = new THREE.PlaneGeometry(2, 2)
    }

    setMaterial() {
        this.material = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                uTime: { value: 0 },
                uResolution: { value: new THREE.Vector2(
                    this.sizes.width  * this.sizes.pixelRatio,
                    this.sizes.height * this.sizes.pixelRatio
                )},
                uMouse: { value: new THREE.Vector2(0.5, 0.5) },
            },
            depthTest:  false,
            depthWrite: false,
        })
    }

    setMesh() {
        this.mesh = new THREE.Mesh(this.geometry, this.material)
        this.mesh.renderOrder = -1
        this.scene.add(this.mesh)
        this.resize()
    }

    setResize() {
        this.sizes.on('resize', () => this.resize())
    }

    // Scales the 2×2 plane to exactly cover the viewport at z=0
    resize() {
        const fov    = this.camera.instance.fov * Math.PI / 180
        const height = 2 * Math.tan(fov / 2) * this.camera.instance.position.z
        const width  = height * (this.sizes.width / this.sizes.height)
        this.mesh.scale.set(width / 2, height / 2, 1)

        this.material.uniforms.uResolution.value.set(
            this.sizes.width  * this.sizes.pixelRatio,
            this.sizes.height * this.sizes.pixelRatio
        )
    }

    update() {
        this.material.uniforms.uTime.value = this.time.elapsed * 0.001
        this.material.uniforms.uMouse.value.set(
            this.mouse.current.x,
            this.mouse.current.y
        )
    }

    destroy() {
        this.scene.remove(this.mesh)
        this.geometry.dispose()
        this.material.dispose()
    }
}
